import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams, useRouteContext } from '@tanstack/react-router';
import { useState, type FormEvent } from 'react';
import { Button, EmptyState, StatusPill, type StatusTone } from '@bb/ui';
import { ApiError, privacyDownloadUrl, type PrivacyRequestView, type TombstoneView } from '../../api/client.ts';
import { usePermissions } from '../access/usePermissions.ts';
import { formatWhen } from '../segments/SegmentsListPage.tsx';

const STATUS: Record<PrivacyRequestView['status'], [string, StatusTone]> = {
  accepted: ['Queued', 'info'],
  running: ['Running', 'info'],
  completed: ['Completed', 'success'],
  failed: ['Failed', 'danger'],
};
const errorText = (error: unknown) => (error instanceof ApiError ? error.message : 'Something went wrong.');

function NewRequest({ environmentId }: { environmentId: string }) {
  const { api } = useRouteContext({ from: '/env/$environmentId/settings/privacy' });
  const queryClient = useQueryClient();
  const permissions = usePermissions();
  const canDelete = permissions.has('profiles.delete');
  const canExport = permissions.has('data.export') && permissions.has('pii.view');
  const [kind, setKind] = useState<'export' | 'delete'>(canExport || !canDelete ? 'export' : 'delete');
  const [externalId, setExternalId] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [reason, setReason] = useState('');
  const create = useMutation({
    mutationFn: () => api.createPrivacyRequest(environmentId, { requestId: crypto.randomUUID(), kind, user: { externalId: externalId.trim() }, ...(reason.trim() ? { reason: reason.trim() } : {}) }),
    onSuccess: () => {
      setExternalId('');
      setConfirmation('');
      setReason('');
      return queryClient.invalidateQueries({ queryKey: ['privacy', environmentId] });
    },
  });
  if (!canDelete && !canExport) return <p className="hint">Exporting needs data.export and pii.view; deleting needs profiles.delete.</p>;
  const confirmed = kind === 'export' || (confirmation.trim() !== '' && confirmation.trim() === externalId.trim());
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (externalId.trim() && confirmed) create.mutate();
  };
  return (
    <form className="stack-form panel" aria-label="New privacy request" onSubmit={submit}>
      <h2>New request</h2>
      <fieldset>
        <legend>Request</legend>
        <label>
          <input type="radio" name="privacy-kind" checked={kind === 'export'} disabled={!canExport} onChange={() => setKind('export')} /> Export the user's data
        </label>
        <label>
          <input type="radio" name="privacy-kind" checked={kind === 'delete'} disabled={!canDelete} onChange={() => setKind('delete')} /> Delete the user
        </label>
      </fieldset>
      <label>
        External ID
        <input value={externalId} onChange={(e) => setExternalId(e.target.value)} />
      </label>
      {kind === 'delete' && (
        <>
          <p className="hint">Deleting is irreversible: the profile, its events, sessions, devices and subscriptions are erased, and the external ID cannot come back until a tombstone is released.</p>
          <label>
            Type the external ID again to confirm
            <input value={confirmation} onChange={(e) => setConfirmation(e.target.value)} />
          </label>
        </>
      )}
      <label>
        Reason
        <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="GDPR request #1234" />
      </label>
      {create.isError && <p role="alert">{errorText(create.error)}</p>}
      <Button type="submit" variant={kind === 'delete' ? 'danger' : 'primary'} disabled={!externalId.trim() || !confirmed || create.isPending}>
        {kind === 'delete' ? 'Delete the user' : 'Export the data'}
      </Button>
    </form>
  );
}

function Tombstones({ environmentId }: { environmentId: string }) {
  const { api } = useRouteContext({ from: '/env/$environmentId/settings/privacy' });
  const [query, setQuery] = useState('');
  const [searched, setSearched] = useState<string | null>(null);
  const [reasons, setReasons] = useState<Record<string, string>>({});
  const queryClient = useQueryClient();
  const found = useQuery({ queryKey: ['privacy', environmentId, 'tombstones', searched], queryFn: () => api.findTombstones(environmentId, searched!), enabled: Boolean(searched) });
  const release = useMutation({
    mutationFn: (tb: TombstoneView) => api.releaseTombstone(environmentId, tb.id, reasons[tb.id]!.trim()),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['privacy', environmentId, 'tombstones'] }),
  });
  return (
    <section aria-label="Tombstones" className="panel">
      <h2>Deleted external IDs</h2>
      <p className="hint">A deleted external ID is refused by ingest until its tombstone is released. Only the hash is stored, so search by the exact ID.</p>
      <form
        className="toolbar"
        onSubmit={(e) => {
          e.preventDefault();
          if (query.trim()) setSearched(query.trim());
        }}
      >
        <label>
          Find external ID <input value={query} onChange={(e) => setQuery(e.target.value)} />
        </label>
        <Button type="submit">Search</Button>
      </form>
      {found.data &&
        (found.data.items.length === 0 ? (
          <p className="hint">No tombstone for this external ID.</p>
        ) : (
          <ul aria-label="Tombstones found">
            {found.data.items.map((tb) => (
              <li key={tb.id}>
                Deleted {formatWhen(tb.deletedAt)}.{' '}
                {tb.releasedAt ? (
                  <span>
                    Released {formatWhen(tb.releasedAt)} by {tb.releasedBy?.email ?? 'someone'}: {tb.releaseReason}
                  </span>
                ) : (
                  <span className="toolbar">
                    <input aria-label="Release reason" value={reasons[tb.id] ?? ''} onChange={(e) => setReasons({ ...reasons, [tb.id]: e.target.value })} />
                    <Button onClick={() => release.mutate(tb)} disabled={!reasons[tb.id]?.trim() || release.isPending}>
                      Release
                    </Button>
                  </span>
                )}
              </li>
            ))}
          </ul>
        ))}
      {release.isError && <p role="alert">{errorText(release.error)}</p>}
    </section>
  );
}

/**
 * Settings → Privacy requests (BB-39): exports and deletes of users, their steps, export downloads, and deleted
 * external IDs (tombstones) with their release. Exports need data.export and pii.view, deletes profiles.delete.
 */
export function PrivacyPage() {
  const { environmentId } = useParams({ from: '/env/$environmentId/settings/privacy' });
  const { api } = useRouteContext({ from: '/env/$environmentId/settings/privacy' });
  const permissions = usePermissions();
  const list = useQuery({
    queryKey: ['privacy', environmentId],
    queryFn: () => api.listPrivacyRequests(environmentId),
    refetchInterval: (q) => (q.state.data?.items.some((r) => r.status === 'accepted' || r.status === 'running') ? 5000 : false),
  });
  return (
    <section>
      <h1>Privacy requests</h1>
      <NewRequest environmentId={environmentId} />
      {list.isError ? (
        <p role="alert">{errorText(list.error)}</p>
      ) : !list.data ? (
        <p aria-busy="true">Loading requests…</p>
      ) : list.data.items.length === 0 ? (
        <EmptyState title="No privacy requests" description="Requests from VCG's backend and from here appear in this list." />
      ) : (
        <table className="table" aria-label="Privacy requests">
          <thead>
            <tr>
              <th scope="col">Created</th>
              <th scope="col">Request</th>
              <th scope="col">Requested by</th>
              <th scope="col">Status</th>
              <th scope="col">Result</th>
            </tr>
          </thead>
          <tbody>
            {list.data.items.map((r) => {
              const [label, tone] = STATUS[r.status];
              return (
                <tr key={r.id}>
                  <td>{formatWhen(r.createdAt)}</td>
                  <td>
                    {r.kind === 'delete' ? 'Delete' : 'Export'} <code>{r.profileId}</code>
                    {r.reason && <div className="hint">{r.reason}</div>}
                  </td>
                  <td>{r.requestedBy.type === 'server_key' ? 'Backend (server key)' : (r.requestedBy.email ?? r.requestedBy.id)}</td>
                  <td>
                    <StatusPill tone={tone}>{label}</StatusPill>
                    {r.steps.length > 0 && <div className="hint">{r.steps.filter((s) => s.status === 'completed').length} of {r.steps.length} steps</div>}
                  </td>
                  <td>
                    {r.export?.available ? (
                      <a href={privacyDownloadUrl(environmentId, r.id)} download>
                        Download export
                      </a>
                    ) : r.kind === 'export' && r.status === 'completed' ? (
                      'Expired'
                    ) : (
                      '—'
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {permissions.has('profiles.delete') && <Tombstones environmentId={environmentId} />}
    </section>
  );
}
